import { useEffect, useRef, useState } from "react";

/**
 * Lightweight WebSocket hook with auto-reconnect.
 * url: absolute wss:// URL (or relative path - will be prefixed)
 * onMessage: (data) => void  — receives parsed JSON
 */
export default function useChatSocket(url, onMessage) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);
  const handlerRef = useRef(onMessage);

  useEffect(() => {
    handlerRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!url) return;
    closedRef.current = false;
    const connect = () => {
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          retryRef.current = 0;
          setConnected(true);
        };
        ws.onmessage = (ev) => {
          if (ev.data === "pong") return;
          try {
            const data = JSON.parse(ev.data);
            handlerRef.current?.(data);
          } catch {}
        };
        ws.onclose = () => {
          setConnected(false);
          wsRef.current = null;
          if (closedRef.current) return;
          const delay = Math.min(8000, 1000 * 2 ** retryRef.current++);
          setTimeout(connect, delay);
        };
        ws.onerror = () => ws.close();
      } catch {
        // ignore
      }
    };
    connect();

    const ping = setInterval(() => {
      if (wsRef.current?.readyState === 1) wsRef.current.send("ping");
    }, 25000);

    return () => {
      closedRef.current = true;
      clearInterval(ping);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [url]);

  return { connected };
}
